import soundcloudConfig from "../../config/soundcloudConfig";
import { fetchJson } from "./http";
import { getSoundCloudClientId } from "./clientIdManager";

const COMMENT_SORT_ALLOWLIST = new Set(["newest", "oldest", "track-timestamp"]);

function buildUrl(path, query = {}) {
  const params = new URLSearchParams();
  params.set("path", String(path));

  Object.entries(query).forEach(([key, value]) => {
    if (value === undefined || value === null || value === "") return;
    params.set(key, String(value));
  });

  return `${soundcloudConfig.API_INTERNAL_ENDPOINT}?${params.toString()}`;
}

function buildAbsoluteUrl(baseUrl, query = {}) {
  const params = new URLSearchParams();
  params.set("targetUrl", String(baseUrl));

  Object.entries(query).forEach(([key, value]) => {
    if (value === undefined || value === null || value === "") return;
    params.set(key, String(value));
  });

  return `${soundcloudConfig.API_INTERNAL_ENDPOINT}?${params.toString()}`;
}

function sanitizeLimit(limit, fallback = 20, max = 50) {
  const parsed = Number(limit);
  if (!Number.isFinite(parsed) || parsed <= 0) return fallback;
  return Math.min(Math.floor(parsed), max);
}

function sanitizeQuery(query) {
  return String(query || "").trim();
}

function chunkArray(items, chunkSize) {
  const chunks = [];
  for (let index = 0; index < items.length; index += chunkSize) {
    chunks.push(items.slice(index, index + chunkSize));
  }
  return chunks;
}

function extractStubTrackIds(tracks = []) {
  return tracks
    .filter((track) => {
      if (!track || typeof track !== "object") return false;
      const hasInvalidTitle = !track.title || track.title === "Unknown Track";
      const hasInvalidDuration = !track.duration || Number(track.duration) <= 0;
      return hasInvalidTitle || hasInvalidDuration;
    })
    .map((track) => Number(track.id))
    .filter((id) => Number.isInteger(id) && id > 0);
}

function normalizeTranscodingProtocol(transcoding) {
  return (
    transcoding?.format?.protocol ||
    transcoding?.format?.protocolName ||
    transcoding?.protocol ||
    ""
  );
}

function sortTranscodingCandidates(candidates) {
  return [...candidates].sort((a, b) => {
    const aSnipped = Boolean(a?.snipped);
    const bSnipped = Boolean(b?.snipped);

    if (aSnipped === bSnipped) return 0;
    return aSnipped ? 1 : -1;
  });
}

async function request(path, query = {}, options = {}) {
  const clientId = await getSoundCloudClientId();

  const url = buildUrl(path, {
    ...query,
    client_id: clientId,
    ...(options.includeLocale === false ? {} : { app_locale: "en" }),
  });

  return fetchJson(url);
}

async function requestAbsolute(absoluteUrl, query = {}, options = {}) {
  const clientId = await getSoundCloudClientId();

  const url = buildAbsoluteUrl(absoluteUrl, {
    ...query,
    client_id: clientId,
    ...(options.includeLocale === false ? {} : { app_locale: "en" }),
  });

  return fetchJson(url);
}

export async function searchTracks(query, options = {}) {
  return request("/search/tracks", {
    q: sanitizeQuery(query),
    limit: sanitizeLimit(options.limit, 20),
    offset: options.offset ?? 0,
    linked_partitioning: options.linkedPartitioning ?? 1,
  });
}

export async function searchUsers(query, options = {}) {
  return request("/search/users", {
    q: sanitizeQuery(query),
    limit: sanitizeLimit(options.limit, 20),
    offset: options.offset ?? 0,
    linked_partitioning: options.linkedPartitioning ?? 1,
  });
}

export async function searchPlaylists(query, options = {}) {
  return request("/search/playlists_without_albums", {
    q: sanitizeQuery(query),
    limit: sanitizeLimit(options.limit, 20),
    offset: options.offset ?? 0,
    linked_partitioning: options.linkedPartitioning ?? 1,
  });
}

export async function searchAlbums(query, options = {}) {
  return request("/search/albums", {
    q: sanitizeQuery(query),
    limit: sanitizeLimit(options.limit, 20),
    offset: options.offset ?? 0,
    linked_partitioning: options.linkedPartitioning ?? 1,
  });
}

export async function getSearchSuggestions(query, options = {}) {
  return request("/search/queries", {
    q: sanitizeQuery(query),
    limit: sanitizeLimit(options.limit, 10, 50),
    offset: options.offset ?? 0,
    linked_partitioning: 1,
  });
}

export async function getTrackDetail(trackId) {
  return request(`/tracks/${encodeURIComponent(String(trackId))}`);
}

export async function getTracksByIds(ids = []) {
  const cleanIds = ids
    .map((id) => Number(id))
    .filter((id) => Number.isInteger(id) && id > 0);

  if (!cleanIds.length) return [];

  const chunks = chunkArray(cleanIds, 50);
  const allTracks = [];

  for (const chunk of chunks) {
    const tracks = await request("/tracks", {
      ids: chunk.join(","),
    });

    if (Array.isArray(tracks)) {
      allTracks.push(...tracks);
    }
  }

  return allTracks;
}

export async function getRelatedTracks(trackId, options = {}) {
  return request(`/tracks/${encodeURIComponent(String(trackId))}/related`, {
    limit: sanitizeLimit(options.limit, 20),
    offset: options.offset ?? 0,
  });
}

export async function getPlaylistDetail(playlistId) {
  return request(`/playlists/${encodeURIComponent(String(playlistId))}`, {
    representation: "full",
  });
}

export async function getPlaylistDetailResolved(playlistId) {
  const playlist = await getPlaylistDetail(playlistId);
  const stubTrackIds = extractStubTrackIds(playlist?.tracks || []);

  if (!stubTrackIds.length) {
    return playlist;
  }

  const fullTracks = await getTracksByIds(stubTrackIds);
  const trackMap = new Map(fullTracks.map((track) => [Number(track.id), track]));

  return {
    ...playlist,
    tracks: (playlist.tracks || []).map((track) => trackMap.get(Number(track.id)) || track),
  };
}

export async function getUserTracks(userId, options = {}) {
  const firstPage = await request(`/users/${encodeURIComponent(String(userId))}/tracks`, {
    limit: sanitizeLimit(options.limit, 20),
    linked_partitioning: 1,
  });

  const firstCollection = firstPage?.collection;
  if (Array.isArray(firstCollection) && firstCollection.length > 0) {
    return firstPage;
  }

  if (firstPage?.next_href) {
    try {
      const nextPage = await fetchJson(firstPage.next_href);
      return nextPage;
    } catch {
      return firstPage;
    }
  }

  return firstPage;
}

export async function getUserPlaylists(userId, options = {}) {
  return request(`/users/${encodeURIComponent(String(userId))}/playlists_without_albums`, {
    limit: sanitizeLimit(options.limit, 20),
  });
}

export async function getUserAlbums(userId, options = {}) {
  return request(`/users/${encodeURIComponent(String(userId))}/albums`, {
    limit: sanitizeLimit(options.limit, 20),
  });
}

export async function getUserDetail(userId) {
  return request(`/users/${encodeURIComponent(String(userId))}`, {}, { includeLocale: false });
}

export async function getComments(trackId, options = {}) {
  const sort = COMMENT_SORT_ALLOWLIST.has(options.sort) ? options.sort : "newest";

  return request(`/tracks/${encodeURIComponent(String(trackId))}/comments`, {
    sort,
    threaded: 1,
    limit: sanitizeLimit(options.limit, 50, 200),
    offset: options.offset ?? 0,
  });
}

export async function getChartsSelections(options = {}) {
  return request("/charts/selections", {
    country: options.country || "all-countries",
    limit: sanitizeLimit(options.limit, 20),
    linked_partitioning: 1,
  });
}

export async function fetchStreamingUrl(track, options = {}) {
  if (!track || typeof track !== "object") {
    throw new Error("Invalid track payload for streaming URL resolution.");
  }

  const trackAuthorization = track.track_authorization;
  const transcodings = track?.media?.transcodings;

  if (!trackAuthorization || !Array.isArray(transcodings) || transcodings.length === 0) {
    throw new Error("Track does not contain streaming authorization metadata.");
  }

  const protocol = options.protocol || "hls";
  const filtered = transcodings.filter(
    (item) => normalizeTranscodingProtocol(item).toLowerCase() === protocol.toLowerCase()
  );

  const candidates = sortTranscodingCandidates(filtered.length ? filtered : transcodings);

  for (const candidate of candidates) {
    if (!candidate?.url) continue;

    try {
      const data = await requestAbsolute(candidate.url, {
        track_authorization: trackAuthorization,
      });

      if (data?.url) {
        return data;
      }
    } catch {
      // Continue with the next transcoding candidate.
    }
  }

  throw new Error("No playable streaming URL found for this track.");
}
